/**
 * FO-041 — Gereedschapspakketten (batch-16, stream uitrusting).
 *
 * Gedeelde enums, label-maps, status-transitie-map, bewijs-regels, request-
 * schemas (Zod) en DTO's voor de tool-pakket-flow: pakket <-> BV/afdeling/
 * functiegroep, portal "Mijn gereedschapspakket" + vervanging-aanvraag,
 * voorraad-reservering, generieke leverancier-bestelmail (default Pehavo) en
 * de onboarding-voorraadcheck.
 *
 * DISJUNCT van `uitrusting.ts` (credit-/Van-Ysselt-bestelmodule) en van
 * `gereedschap.ts` (individuele tool-exemplaren op de voorraad-uniek-engine).
 * Pakketinhoud = `VoorraadArtikel` (magazijn), niet de credit-catalogus.
 *
 * ID-referenties: `z.string().min(1)` (prod heeft legacy UUID-ids → NIET
 * `.cuid()`). Geld als Prisma Decimal server-side; in schemas als
 * `z.number()`. Datums als ISO/`yyyy-mm-dd`-string; de route parset naar Date
 * (Europe/Amsterdam op de UI-rand).
 */

import { z } from 'zod'
import { multiEmailSchema } from './email'
import { partialZonderDefaults } from './zod-patch'

// ─────────────────────────────────────────────────────────────────────────
// Enums + labels (spiegelt de Prisma-enums)
// ─────────────────────────────────────────────────────────────────────────

export const GEREEDSCHAPS_AANVRAAG_STATUS = [
  'CONCEPT',
  'INGEDIEND',
  'WACHT_TL',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'IN_BEHANDELING_VOORRAAD',
  'BESTELLEN_NODIG',
  'BESTELD',
  'GELEVERD',
  'UITGEGEVEN',
  'AFGEROND',
  'GEANNULEERD',
] as const
export type GereedschapsAanvraagStatus = (typeof GEREEDSCHAPS_AANVRAAG_STATUS)[number]

export const GEREEDSCHAPS_AANVRAAG_STATUS_LABELS: Record<GereedschapsAanvraagStatus, string> = {
  CONCEPT: 'Concept',
  INGEDIEND: 'Ingediend',
  WACHT_TL: 'Wacht op teamleider',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgekeurd',
  IN_BEHANDELING_VOORRAAD: 'Gereserveerd uit voorraad',
  BESTELLEN_NODIG: 'Bestellen nodig',
  BESTELD: 'Besteld',
  GELEVERD: 'Geleverd',
  UITGEGEVEN: 'Uitgegeven',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
}

/** Statussen die als "afgehandeld/terminaal" tellen (geen verdere mutatie). */
export const GEREEDSCHAPS_AANVRAAG_EIND_STATUS: GereedschapsAanvraagStatus[] = [
  'AFGEKEURD',
  'AFGEROND',
  'GEANNULEERD',
]

/** Statussen die de TL in de beoordeel-inbox ziet (te beoordelen). */
export const GEREEDSCHAPS_AANVRAAG_TE_BEOORDELEN: GereedschapsAanvraagStatus[] = [
  'INGEDIEND',
  'WACHT_TL',
]

/**
 * Toegestane status-overgangen (state machine). Sleutel = van-status,
 * waarde = naar-statussen. Server-side gevalideerd in de aanvraag-flow.
 */
export const GEREEDSCHAPS_AANVRAAG_TRANSITIES: Record<
  GereedschapsAanvraagStatus,
  GereedschapsAanvraagStatus[]
> = {
  CONCEPT: ['INGEDIEND', 'GEANNULEERD'],
  INGEDIEND: ['WACHT_TL', 'GEANNULEERD'],
  WACHT_TL: ['GOEDGEKEURD', 'AFGEKEURD', 'GEANNULEERD'],
  GOEDGEKEURD: ['IN_BEHANDELING_VOORRAAD', 'BESTELLEN_NODIG', 'GEANNULEERD'],
  AFGEKEURD: [],
  IN_BEHANDELING_VOORRAAD: ['BESTELLEN_NODIG', 'UITGEGEVEN', 'GEANNULEERD'],
  BESTELLEN_NODIG: ['BESTELD', 'IN_BEHANDELING_VOORRAAD', 'GEANNULEERD'],
  BESTELD: ['GELEVERD', 'GEANNULEERD'],
  GELEVERD: ['IN_BEHANDELING_VOORRAAD', 'UITGEGEVEN', 'GEANNULEERD'],
  UITGEGEVEN: ['AFGEROND'],
  AFGEROND: [],
  GEANNULEERD: [],
}

export function magGereedschapsAanvraagOvergaan(
  van: GereedschapsAanvraagStatus,
  naar: GereedschapsAanvraagStatus,
): boolean {
  return GEREEDSCHAPS_AANVRAAG_TRANSITIES[van]?.includes(naar) ?? false
}

export const GEREEDSCHAPS_AANVRAAG_REDEN = [
  'KAPOT',
  'KWIJT',
  'VERSLETEN',
  'ONTBREEKT',
  'EXTRA',
  'ANDERS',
] as const
export type GereedschapsAanvraagReden = (typeof GEREEDSCHAPS_AANVRAAG_REDEN)[number]

export const GEREEDSCHAPS_AANVRAAG_REDEN_LABELS: Record<GereedschapsAanvraagReden, string> = {
  KAPOT: 'Kapot',
  KWIJT: 'Kwijt',
  VERSLETEN: 'Versleten',
  ONTBREEKT: 'Ontbreekt',
  EXTRA: 'Extra nodig',
  ANDERS: 'Anders',
}

/**
 * Bewijs-eisen per reden (één bron voor client + server):
 * - foto verplicht bij KAPOT/VERSLETEN;
 * - toelichting (opmerking) verplicht bij KWIJT/EXTRA/ANDERS.
 */
export function aanvraagRegelBewijsVereist(reden: GereedschapsAanvraagReden): {
  fotoVereist: boolean
  toelichtingVereist: boolean
} {
  return {
    fotoVereist: reden === 'KAPOT' || reden === 'VERSLETEN',
    toelichtingVereist: reden === 'KWIJT' || reden === 'EXTRA' || reden === 'ANDERS',
  }
}

export const GEREEDSCHAPS_AANVRAAG_REGEL_STATUS = ['OPEN', 'GOEDGEKEURD', 'AFGEKEURD'] as const
export type GereedschapsAanvraagRegelStatus =
  (typeof GEREEDSCHAPS_AANVRAAG_REGEL_STATUS)[number]

// Spiegelt de Prisma-enum `VoorraadReserveringBron`. UITRUSTING_BESTELLING kwam
// erbij met de uitrusting-verwerkstap: reserveren gebeurt daar bij het bevestigen,
// uitgeven pas bij het afmelden van de picklijst.
// batch-69 samenstellen (FO-510): SAMENSTELLING = reservering voor een
// samenstel-regel zonder aanvraag (koffer vullen, set-regel, welkomstpakket).
export const VOORRAAD_RESERVERING_BRON = ['AANVRAAG', 'ONBOARDING', 'UITRUSTING_BESTELLING', 'SAMENSTELLING'] as const
export type VoorraadReserveringBron = (typeof VOORRAAD_RESERVERING_BRON)[number]

export const GEREEDSCHAP_DOORBELASTING_KEUZE = ['NIET', 'WEL', 'LATER'] as const
export type GereedschapDoorbelastingKeuze = (typeof GEREEDSCHAP_DOORBELASTING_KEUZE)[number]

export const GEREEDSCHAP_DOORBELASTING_KEUZE_LABELS: Record<
  GereedschapDoorbelastingKeuze,
  string
> = {
  NIET: 'Niet doorbelasten',
  WEL: 'Wel doorbelasten',
  LATER: 'Later beslissen',
}

export const LEVERANCIER_BESTELLING_STATUS = ['CONCEPT', 'VERZONDEN', 'GEANNULEERD'] as const
export type LeverancierBestellingStatus = (typeof LEVERANCIER_BESTELLING_STATUS)[number]

export const LEVERANCIER_BESTELLING_STATUS_LABELS: Record<LeverancierBestellingStatus, string> = {
  CONCEPT: 'Concept',
  VERZONDEN: 'Verzonden',
  GEANNULEERD: 'Geannuleerd',
}

/** Live voorraadstatus per pakket-/aanvraagregel (afgeleid, niet opgeslagen). */
export const GEREEDSCHAP_VOORRAAD_STATUS = ['OP_VOORRAAD', 'TE_BESTELLEN', 'ONBEKEND'] as const
export type GereedschapVoorraadStatus = (typeof GEREEDSCHAP_VOORRAAD_STATUS)[number]

/** Default-leverancier (Pehavo) — fallback als geen leverancier geconfigureerd is. */
export const PEHAVO_DEFAULT = {
  naam: 'Pehavo',
  email: 'T.Schoonenberg@pehavo.nl',
} as const

// ─────────────────────────────────────────────────────────────────────────
// Request-schemas (gedeeld: API `.parse`/`.safeParse` + form `.safeParse`)
// ─────────────────────────────────────────────────────────────────────────

const idRef = z.string().min(1)
const dateString = z.string().min(1).max(40)

/** Eén aanvraagregel bij het aanmaken/bijwerken van een vervanging-aanvraag. */
export const gereedschapsAanvraagRegelInputSchema = z
  .object({
    voorraadArtikelId: idRef,
    aantal: z.number().int().min(1).max(999),
    reden: z.enum(GEREEDSCHAPS_AANVRAAG_REDEN),
    // Toelichting bij KWIJT/EXTRA/ANDERS (foto loopt apart via multipart-upload,
    // de fotoVereist-check zit op de indienen-stap server-side + client-side).
    opmerking: z.string().trim().max(2000).optional().nullable(),
  })
  .superRefine((v, ctx) => {
    if (aanvraagRegelBewijsVereist(v.reden).toelichtingVereist) {
      if (!v.opmerking || v.opmerking.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['opmerking'],
          message: 'Vul een toelichting in bij deze reden',
        })
      }
    }
  })
export type GereedschapsAanvraagRegelInput = z.infer<typeof gereedschapsAanvraagRegelInputSchema>

export const createGereedschapsAanvraagSchema = z.object({
  // Optioneel: aanvraag tegen een specifiek pakket (anders los/extra).
  pakketId: idRef.optional().nullable(),
  gewensteLeverdatum: dateString.optional().nullable(),
  regels: z.array(gereedschapsAanvraagRegelInputSchema).min(1, 'Voeg minimaal één regel toe'),
})
export type CreateGereedschapsAanvraagInput = z.infer<typeof createGereedschapsAanvraagSchema>

export const updateGereedschapsAanvraagSchema = z.object({
  gewensteLeverdatum: dateString.optional().nullable(),
  regels: z.array(gereedschapsAanvraagRegelInputSchema).min(1, 'Voeg minimaal één regel toe'),
})
export type UpdateGereedschapsAanvraagInput = z.infer<typeof updateGereedschapsAanvraagSchema>

/** TL-beoordeling per regel (gedeeltelijke goedkeuring + aantal aanpassen). */
export const beoordeelRegelSchema = z
  .object({
    regelId: idRef,
    besluit: z.enum(['GOEDGEKEURD', 'AFGEKEURD']),
    aantalGoedgekeurd: z.number().int().min(0).max(999).optional().nullable(),
    afkeurReden: z.string().trim().max(2000).optional().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.besluit === 'AFGEKEURD' && (!v.afkeurReden || v.afkeurReden.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['afkeurReden'],
        message: 'Geef een reden bij afkeuren',
      })
    }
    if (
      v.besluit === 'GOEDGEKEURD' &&
      v.aantalGoedgekeurd !== undefined &&
      v.aantalGoedgekeurd !== null &&
      v.aantalGoedgekeurd < 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['aantalGoedgekeurd'],
        message: 'Goedgekeurd aantal moet minimaal 1 zijn',
      })
    }
  })
export type BeoordeelRegelInput = z.infer<typeof beoordeelRegelSchema>

export const beoordeelGereedschapsAanvraagSchema = z
  .object({
    notitie: z.string().trim().max(2000).optional().nullable(),
    regels: z.array(beoordeelRegelSchema).min(1, 'Geen regels om te beoordelen'),
  })
  .superRefine((v, ctx) => {
    const alleAfgekeurd = v.regels.every((r) => r.besluit === 'AFGEKEURD')
    if (alleAfgekeurd && (!v.notitie || v.notitie.trim().length === 0)) {
      // Spiegelt de bestaande uitrusting-regel "afkeuren = notitie verplicht".
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notitie'],
        message: 'Geef een toelichting bij een volledige afkeuring',
      })
    }
  })
export type BeoordeelGereedschapsAanvraagInput = z.infer<
  typeof beoordeelGereedschapsAanvraagSchema
>

/** Kwijt-doorbelasting-keuze (TL/HR) op een regel. Voedt FO-033 (employee-kosten). */
export const gereedschapDoorbelastingSchema = z
  .object({
    regelId: idRef,
    keuze: z.enum(GEREEDSCHAP_DOORBELASTING_KEUZE),
    reden: z.string().trim().max(2000).optional().nullable(),
    bedrag: z.number().nonnegative().max(1_000_000).optional().nullable(),
    akkoord: z.boolean().optional().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.keuze === 'WEL') {
      if (v.bedrag === undefined || v.bedrag === null || v.bedrag <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bedrag'],
          message: 'Vul het door te belasten bedrag in',
        })
      }
      if (!v.reden || v.reden.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reden'],
          message: 'Geef een reden voor de doorbelasting',
        })
      }
      if (!v.akkoord) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['akkoord'],
          message: 'Bevestig akkoord voor doorbelasting',
        })
      }
    }
  })
export type GereedschapDoorbelastingInput = z.infer<typeof gereedschapDoorbelastingSchema>

/**
 * Uitgifte: boekt de bij goedkeuring gemaakte reserveringen af en koppelt ze
 * aan de medewerker (uniek = het gereserveerde exemplaar, bulk = aantal).
 */
export const geefGereedschapsAanvraagUitSchema = z.object({
  notitie: z.string().trim().max(2000).optional().nullable(),
})
export type GeefGereedschapsAanvraagUitInput = z.infer<typeof geefGereedschapsAanvraagUitSchema>

// ── Pakketbeheer ────────────────────────────────────────────────────────────

// batch-69 samenstellen (FO-510 §A) — template-typen en gedrag (spiegel van Prisma).
// batch-70 uitrusting-controle (FO-531 A6): ACCUGEREEDSCHAP = de standaardset accugereedschap
// (losse machines, accu's en laders in bruikleen); de uitrustingscontrole herkent er het
// accugereedschap van een medewerker aan.
export const PAKKET_TYPES = ['GEREEDSCHAP', 'PBM_KOFFER', 'WELKOMST', 'ACCUGEREEDSCHAP'] as const
export type PakketType = (typeof PAKKET_TYPES)[number]
export const PAKKET_TYPE_LABELS: Record<PakketType, string> = {
  GEREEDSCHAP: 'Gereedschap',
  PBM_KOFFER: 'PBM-koffer',
  WELKOMST: 'Welkomstpakket',
  ACCUGEREEDSCHAP: 'Accugereedschap',
}

export const PAKKET_UITGIFTEMODI = ['COMPLEET', 'DEELS_TOEGESTAAN'] as const
export type PakketUitgiftemodus = (typeof PAKKET_UITGIFTEMODI)[number]
export const PAKKET_UITGIFTEMODUS_LABELS: Record<PakketUitgiftemodus, string> = {
  COMPLEET: 'Compleet (alles of niets)',
  DEELS_TOEGESTAAN: 'Deels toegestaan',
}

export const PAKKET_EIGENDOMMEN = ['BRUIKLEEN', 'EIGENDOM_MEDEWERKER'] as const
export type PakketEigendom = (typeof PAKKET_EIGENDOMMEN)[number]
export const PAKKET_EIGENDOM_LABELS: Record<PakketEigendom, string> = {
  BRUIKLEEN: 'Bruikleen (inname bij uitdienst)',
  EIGENDOM_MEDEWERKER: 'Eigendom medewerker',
}

export interface PakketGedrag {
  type: PakketType
  alsSetOpVoorraad: boolean
  uitgiftemodus: PakketUitgiftemodus
  eigendom: PakketEigendom
  credits: boolean
  minimumSets: number
}

/**
 * Het gedrag dat bij een template-type hoort, afgedwongen bij opslaan (API) en
 * getoond in het formulier (zelfde functie, één waarheid):
 *   - PBM_KOFFER is altijd een complete set op voorraad (eigen QR), bruikleen;
 *   - WELKOMST is altijd eigendom van de medewerker, nooit een set, zonder credits;
 *   - een minimum aan sets bestaat alleen bij "als set op voorraad".
 */
export function normaliseerPakketGedrag(invoer: Partial<PakketGedrag> & { type?: PakketType | null }): PakketGedrag {
  const type: PakketType = invoer.type ?? 'GEREEDSCHAP'
  const alsSet = type === 'PBM_KOFFER' ? true : type === 'WELKOMST' ? false : invoer.alsSetOpVoorraad ?? false
  return {
    type,
    alsSetOpVoorraad: alsSet,
    uitgiftemodus: invoer.uitgiftemodus ?? 'COMPLEET',
    eigendom: type === 'WELKOMST' ? 'EIGENDOM_MEDEWERKER' : type === 'PBM_KOFFER' ? 'BRUIKLEEN' : invoer.eigendom ?? 'BRUIKLEEN',
    credits: type === 'WELKOMST' ? false : invoer.credits ?? false,
    minimumSets: alsSet ? Math.max(0, Math.floor(invoer.minimumSets ?? 0)) : 0,
  }
}

export const gereedschapsPakketDoelgroepInputSchema = z
  .object({
    entityId: idRef.optional().nullable(),
    departmentId: idRef.optional().nullable(),
    functieGroep: z.string().trim().max(200).optional().nullable(),
  })
  .superRefine((v, ctx) => {
    const gevuld = [v.entityId, v.departmentId, v.functieGroep].filter(
      (x) => x !== undefined && x !== null && String(x).trim().length > 0,
    ).length
    if (gevuld !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entityId'],
        message: 'Kies precies één doelgroep-dimensie (BV, afdeling of functiegroep)',
      })
    }
  })
export type GereedschapsPakketDoelgroepInput = z.infer<
  typeof gereedschapsPakketDoelgroepInputSchema
>

export const gereedschapsPakketRegelInputSchema = z.object({
  voorraadArtikelId: idRef,
  aantal: z.number().int().min(1).max(999),
})
export type GereedschapsPakketRegelInput = z.infer<typeof gereedschapsPakketRegelInputSchema>

export const createGereedschapsPakketSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  leverancierId: idRef.optional().nullable(),
  actief: z.boolean().optional().default(true),
  doelgroepen: z.array(gereedschapsPakketDoelgroepInputSchema).optional().default([]),
  regels: z.array(gereedschapsPakketRegelInputSchema).optional().default([]),
  // batch-69 samenstellen (FO-510 §A) — bewust zonder `.default()`: het
  // PATCH-schema is `.partial()` en een default zou een weggelaten veld bij elke
  // wijziging terugzetten. De create-route vult de defaults via
  // `normaliseerPakketGedrag`.
  type: z.enum(PAKKET_TYPES).optional(),
  alsSetOpVoorraad: z.boolean().optional(),
  uitgiftemodus: z.enum(PAKKET_UITGIFTEMODI).optional(),
  eigendom: z.enum(PAKKET_EIGENDOMMEN).optional(),
  credits: z.boolean().optional(),
  minimumSets: z.number().int().min(0).max(999).optional(),
})
export type CreateGereedschapsPakketInput = z.infer<typeof createGereedschapsPakketSchema>

export const updateGereedschapsPakketSchema = partialZonderDefaults(createGereedschapsPakketSchema)
export type UpdateGereedschapsPakketInput = z.infer<typeof updateGereedschapsPakketSchema>

/** Handmatige pakket-override op een medewerker (null = override wissen). */
export const setGereedschapsPakketOverrideSchema = z.object({
  pakketId: idRef.nullable(),
})
export type SetGereedschapsPakketOverrideInput = z.infer<
  typeof setGereedschapsPakketOverrideSchema
>

// ── Leverancier + bestelmail ─────────────────────────────────────────────────

export const upsertUitrustingLeverancierSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  // Eén of meerdere komma-gescheiden bestelmail-ontvangers (de verzendkant
  // splitst hierop). Spiegelt read↔write zodat de UI nooit een adres-lijst
  // weigert die de mailer wél verstuurt.
  email: multiEmailSchema,
  contactpersoon: z.string().trim().max(200).optional().nullable(),
  replyTo: z.string().trim().email('Ongeldig reply-to-adres').max(200).optional().nullable(),
  mailOnderwerp: z.string().trim().max(500).optional().nullable(),
  mailBody: z.string().trim().max(8000).optional().nullable(),
  // FO-059-vervolg — optionele koppeling naar de relatie (Client) van deze leverancier.
  clientId: z.string().trim().min(1).max(50).optional().nullable(),
  isDefault: z.boolean().optional().default(false),
  actief: z.boolean().optional().default(true),
})
export type UpsertUitrustingLeverancierInput = z.infer<typeof upsertUitrustingLeverancierSchema>

/** Concept-bestelmail bewerken vóór verzending. */
export const updateLeverancierBestellingConceptSchema = z.object({
  conceptOnderwerp: z.string().trim().min(1).max(500),
  conceptBody: z.string().trim().min(1).max(20000),
  gewensteLeverdatum: dateString.optional().nullable(),
})
export type UpdateLeverancierBestellingConceptInput = z.infer<
  typeof updateLeverancierBestellingConceptSchema
>

/**
 * batch-48 (spec 97 §3.2) — bestelmail versturen. Zonder `bevestigDuplicaat`
 * blokkeert de kopie-detectie het versturen zodra artikelen uit deze bestelling
 * ook in een open, al verstuurde bestelling bij dezelfde leverancier staan.
 * Doorzetten kan (naleveringen bestaan) en wordt vastgelegd met actor + reden.
 */
export const verstuurLeverancierBestellingSchema = z.object({
  bevestigDuplicaat: z.boolean().optional().default(false),
  duplicaatReden: z.string().trim().max(500).optional().nullable(),
})
export type VerstuurLeverancierBestellingInput = z.infer<
  typeof verstuurLeverancierBestellingSchema
>

/**
 * batch-48 (spec 97 §3.1/§3.4) — bestelling annuleren. Een AL VERZONDEN
 * bestelling annuleren mag, en moet ook kunnen: levert de leverancier nooit,
 * dan blijft de behoefte anders voorgoed geblokkeerd (de bron-regel staat
 * `IN_BESTELLING` en wordt in elke ronde overgeslagen). Omdat de leverancier de
 * mail al heeft, vraagt dat wél een expliciete bevestiging: `bevestigVerzonden`.
 * De annulering wordt met actor + reden vastgelegd.
 */
export const annuleerLeverancierBestellingSchema = z.object({
  bevestigVerzonden: z.boolean().optional().default(false),
  reden: z.string().trim().max(500).optional().nullable(),
})
export type AnnuleerLeverancierBestellingInput = z.infer<
  typeof annuleerLeverancierBestellingSchema
>

/** Onboarding: pakket klaarzetten (reserveren + bestelvoorstel + taak + conceptmail). */
export const onboardingGereedschapKlaarzetSchema = z.object({
  gewensteLeverdatum: dateString.optional().nullable(),
})
export type OnboardingGereedschapKlaarzetInput = z.infer<
  typeof onboardingGereedschapKlaarzetSchema
>

// ─────────────────────────────────────────────────────────────────────────
// DTO's
// ─────────────────────────────────────────────────────────────────────────

export interface GereedschapVoorraadInfoDTO {
  /** Vrije voorraad over alle locaties (aantal - gereserveerd) voor bulk-artikelen. */
  vrij: number
  gereserveerd: number
  /** Voor unieke artikelen: aantal beschikbare (on-voorraad, niet-gereserveerde) exemplaren. */
  beschikbaarUniek: number | null
  traceerbaar: boolean
  status: GereedschapVoorraadStatus
}

export interface MijnPakketRegelDTO {
  voorraadArtikelId: string
  artikelNummer: string
  naam: string
  omschrijving: string | null
  categorie: string
  aantal: number
  fotoUrl: string | null
  leverancier: string | null
  leverancierArtNr: string | null
  voorraadStatus: GereedschapVoorraadStatus
}

export interface MijnGereedschapsPakketDTO {
  pakket: { id: string; naam: string; omschrijving: string | null } | null
  /** Waarom geen pakket bepaald kon worden (geen BV/koppeling) - voor de UI-hint. */
  geenPakketReden: string | null
  /** Bron van de pakketbepaling: OVERRIDE | BV | AFDELING | FUNCTIEGROEP. */
  bepaaldVia: 'OVERRIDE' | 'BV' | 'AFDELING' | 'FUNCTIEGROEP' | null
  regels: MijnPakketRegelDTO[]
  openAanvragen: GereedschapsAanvraagSamenvattingDTO[]
}

export interface GereedschapsAanvraagSamenvattingDTO {
  id: string
  status: GereedschapsAanvraagStatus
  statusLabel: string
  aantalRegels: number
  ingediendOp: string | null
  createdAt: string
}

export interface GereedschapsAanvraagRegelDTO {
  id: string
  voorraadArtikelId: string
  artikelNummer: string
  naam: string
  categorie: string
  aantal: number
  aantalGoedgekeurd: number | null
  reden: GereedschapsAanvraagReden
  redenLabel: string
  fotoUrl: string | null
  opmerking: string | null
  status: GereedschapsAanvraagRegelStatus
  afkeurReden: string | null
  voorraad: GereedschapVoorraadInfoDTO
  doorbelasting: {
    keuze: GereedschapDoorbelastingKeuze | null
    reden: string | null
    bedrag: string | null
    akkoord: boolean
  }
}

export interface GereedschapsAanvraagGebeurtenisDTO {
  id: string
  actie: string
  vanStatus: GereedschapsAanvraagStatus | null
  naarStatus: GereedschapsAanvraagStatus | null
  notitie: string | null
  createdAt: string
}

export interface GereedschapsAanvraagDTO {
  id: string
  employeeId: string
  employeeNaam: string
  employeeNummer: string | null
  pakketId: string | null
  pakketNaam: string | null
  bron: 'AANVRAAG' | 'ONBOARDING'
  status: GereedschapsAanvraagStatus
  statusLabel: string
  onboardingId: string | null
  gewensteLeverdatum: string | null
  ingediendOp: string | null
  beoordeeldOp: string | null
  beoordeeldDoorNaam: string | null
  beoordelingNotitie: string | null
  uitgegevenOp: string | null
  createdAt: string
  regels: GereedschapsAanvraagRegelDTO[]
  gebeurtenissen: GereedschapsAanvraagGebeurtenisDTO[]
  bestellingen: LeverancierBestellingSamenvattingDTO[]
}

export interface GereedschapsPakketDoelgroepDTO {
  id: string
  entityId: string | null
  entityNaam: string | null
  departmentId: string | null
  departmentNaam: string | null
  functieGroep: string | null
}

export interface GereedschapsPakketRegelDTO {
  id: string
  voorraadArtikelId: string
  artikelNummer: string
  naam: string
  categorie: string
  aantal: number
  leverancier: string | null
  leverancierArtNr: string | null
  minimumVoorraad: number
  bestelbaar: boolean
  voorraad: GereedschapVoorraadInfoDTO
}

export interface GereedschapsPakketDTO {
  id: string
  naam: string
  omschrijving: string | null
  actief: boolean
  leverancierId: string | null
  leverancierNaam: string | null
  doelgroepen: GereedschapsPakketDoelgroepDTO[]
  regels: GereedschapsPakketRegelDTO[]
  createdAt: string
  updatedAt: string
  // batch-69 samenstellen (FO-510 §A/§D) — type, gedrag en het voorraadbeeld.
  type: PakketType
  alsSetOpVoorraad: boolean
  uitgiftemodus: PakketUitgiftemodus
  eigendom: PakketEigendom
  credits: boolean
  minimumSets: number
  /** Kostprijs per pakket (som inkoopprijs x aantal, informatief, Decimal als string); null = geen enkele prijs bekend. */
  kostprijs: string | null
  /** Ontbreekt van minstens een regel de inkoopprijs (kostprijs is dan een ondergrens)? */
  kostprijsOnvolledig: boolean
  /** Set-voorraad (alleen bij alsSetOpVoorraad). */
  sets: { compleet: number; inSamenstelling: number; uitgegeven: number; inControle: number } | null
  /** Uitgiftes van dit pakket in de laatste 12 maanden. */
  uitgegeven12m: number
}

export interface UitrustingLeverancierDTO {
  id: string
  naam: string
  email: string | null
  contactpersoon: string | null
  replyTo: string | null
  mailOnderwerp: string | null
  mailBody: string | null
  isDefault: boolean
  actief: boolean
  /** FO-059-vervolg — optioneel gekoppelde relatie (Client). */
  clientId: string | null
  clientNaam: string | null
  aantalPakketten: number
}

export interface LeverancierBestellingRegelDTO {
  id: string
  voorraadArtikelId: string
  artikelNaam: string
  leverancierArtNr: string | null
  aantal: number
}

export interface LeverancierBestellingSamenvattingDTO {
  id: string
  status: LeverancierBestellingStatus
  statusLabel: string
  leverancierNaam: string
  aantalRegels: number
  verzondenOp: string | null
  createdAt: string
}

export interface LeverancierBestellingDTO extends LeverancierBestellingSamenvattingDTO {
  leverancierId: string
  leverancierEmail: string | null
  bron: 'AANVRAAG' | 'ONBOARDING' | 'HANDMATIG'
  employeeId: string | null
  employeeNaam: string | null
  aanvraagId: string | null
  onboardingId: string | null
  gewensteLeverdatum: string | null
  conceptOnderwerp: string | null
  conceptBody: string | null
  regels: LeverancierBestellingRegelDTO[]
  /** batch-48 — leesbaar kenmerk (BEST-A1B2C3) van deze bestelling. */
  kenmerk: string
  /** batch-48 (§3.2) — bewust doorgezet duplicaat: wanneer, door wie, waarom. */
  duplicaatBevestigdOp: string | null
  duplicaatBevestigdDoorNaam: string | null
  duplicaatReden: string | null
}

/**
 * batch-48 (spec 97 §3.1) — compacte samenvatting van wat een bundelpad heeft
 * OVERGESLAGEN omdat de behoefte al in een open bestelling stond. Elke UI die
 * "besteld" meldt moet dit tonen; anders is het overslaan stil, en dat is
 * precies het spiegelbeeld van de bug die deze stream oplost.
 */
export interface OvergeslagenBehoefteDTO {
  /** Aantal stuks dat niet besteld is omdat het al openstond. */
  aantal: number
  /** Kenmerken van de openstaande bestelling(en), bv. ["BEST-A1B2C3"]. */
  kenmerken: string[]
}

/** Uitkomst van "extern bestellen voor een medewerker" (FO-373 + batch-48). */
export interface BestelExternResultaatDTO {
  aanvraagId: string
  /** Null wanneer er niets besteld is omdat de behoefte al openstond. */
  bestelvoorstelId: string | null
  artikelNaam: string
  overgeslagen: OvergeslagenBehoefteDTO | null
}

// ─── batch-48 (spec 97 §3.2) — kopie-detectie bij versturen ─────────────────

/** Eén open bestelling waarmee de te versturen bestelling regels deelt. */
export interface KopieOverlapBestellingDTO {
  bestellingId: string
  kenmerk: string
  status: LeverancierBestellingStatus
  verzondenOp: string | null
  regels: Array<{ voorraadArtikelId: string; artikelNaam: string; aantal: number }>
}

/**
 * Payload van de blokkerende waarschuwing (HTTP 409, code
 * `BESTELLING_MOGELIJK_DUPLICAAT`). `waarschuwing` is de kant-en-klare
 * spec-tekst; de UI toont die en biedt "toch versturen" met optionele reden.
 */
export interface KopieDetectieDTO {
  leverancierNaam: string
  aantalOverlappendeRegels: number
  waarschuwing: string
  bestellingen: KopieOverlapBestellingDTO[]
}

// ─── batch-48 (spec 97 §3.3) — mailhistorie per leverancier ─────────────────

/** Eén verstuurde bestelmail aan deze leverancier. */
export interface LeverancierMailhistorieRegelDTO {
  bestellingId: string
  kenmerk: string
  /** Verzendmoment (ISO); valt terug op de aanmaakdatum als het ontbreekt. */
  verzondenOp: string
  /**
   * Huidige status van de bestelling. Een later GEANNULEERDE bestelling blijft
   * in de historie staan: de leverancier heeft die mail wél ontvangen (en juist
   * de opgeruimde duplicaten moeten terugvindbaar blijven, spec §6.5).
   */
  bestellingStatus: LeverancierBestellingStatus
  bestellingStatusLabel: string
  onderwerp: string | null
  /** Aflever-status van de mail zelf (uit de uitbox), of null bij een oude rij. */
  mailStatus: string | null
  mailStatusLabel: string
  /** Waar deze bestelling vandaan kwam (controle-afwijking / pakket-tekort / handmatig). */
  bronLabel: string
  medewerkerNaam: string | null
  aantalRegels: number
  regels: LeverancierBestellingRegelDTO[]
  /** Bewust doorgezet duplicaat (§3.2). */
  duplicaatDoorgezet: boolean
  duplicaatReden: string | null
}

export interface LeverancierMailhistorieDTO {
  leverancierId: string
  leverancierNaam: string
  regels: LeverancierMailhistorieRegelDTO[]
  page: number
  pageSize: number
  total: number
}

/** Resultaat van de onboarding-voorraadcheck op de volledige pakketinhoud. */
export interface OnboardingGereedschapCheckDTO {
  pakketId: string | null
  pakketNaam: string | null
  geenPakketReden: string | null
  regels: Array<{
    voorraadArtikelId: string
    naam: string
    aantalNodig: number
    aantalOpVoorraad: number
    tekort: number
    status: GereedschapVoorraadStatus
  }>
  aantalTekort: number
  leverancierNaam: string
}

/** Resultaat van het klaarzetten van het onboarding-gereedschapspakket. */
export interface OnboardingGereedschapKlaarzetResultaatDTO {
  aanvraagId: string | null
  geenPakketReden: string | null
  aantalGereserveerd: number
  aantalTekort: number
  bestelvoorstelId: string | null
  taakId: string | null
  /** batch-48 (§3.1) — deel van het tekort dat al in een open bestelling stond. */
  overgeslagen: OvergeslagenBehoefteDTO | null
}
